import { commonUtils } from '@sato/components';
import { useQuery } from '@tanstack/react-query';

import { useLanguage } from '@hooks/useTranslate';
import { LanguageKey } from '@models/Language';

import { contentfulClient } from '../api/client.contentful';
import { StaticContentfulEntry } from '../contentful.constants';
import { CONTENT_TYPE, EntryType, ParsedEntry } from '../types';

interface Conf {
  include: number;
  locale: LanguageKey;
  content_type: CONTENT_TYPE;
}

type ContentfulConfig = Partial<Conf> & Record<string, unknown>;

export const fetchEntries = async <EntryT extends EntryType>(
  config: ContentfulConfig
): Promise<ParsedEntry<EntryT>[]> => {
  const response = await contentfulClient.getParsedEntries<EntryT>(config);

  return response.filter(commonUtils.nonNullable);
};

export const fetchEntryById = async <EntryT extends EntryType>(
  id: string,
  locale: LanguageKey
) =>
  contentfulClient.getParsedEntry<EntryT>(id, {
    include: 10,
    locale,
  });

export const fetchEntry = async <EntryT extends EntryType>(
  config: ContentfulConfig
): Promise<ParsedEntry<EntryT>> => {
  const result = await fetchEntries<EntryT>(config);

  if (!result?.[0]) throw new Error('Entry not found');

  return result[0];
};

export const useContentfulEntry = <EntryT extends EntryType>(
  configOrId: ContentfulConfig | StaticContentfulEntry
) => {
  const language = useLanguage();

  return useQuery<ParsedEntry<EntryT> | null>(
    ['entry', configOrId, language],
    () =>
      typeof configOrId === 'string'
        ? fetchEntryById<EntryT>(configOrId, language)
        : fetchEntry<EntryT>({ ...configOrId, locale: language })
  );
};

export const useContentfulEntries = <EntryT extends EntryType>(
  config: ContentfulConfig
) => useQuery(['entries', config], () => fetchEntries<EntryT>(config));
